import {
    TextField as MuiTextField,
    TextFieldProps as MuiTextFieldProps,
    styled,
} from "@mui/material";
import React from "react";

interface TextFieldProps extends Omit<MuiTextFieldProps, "size" | "variant"> {
    disableFocusShadow?: boolean;
}

const StyledTextField = styled(MuiTextField, {
    shouldForwardProp: (prop) => prop !== "disableFocusShadow",
})<{ disableFocusShadow?: boolean }>(({ disableFocusShadow, theme }) => ({
    "& .MuiInputLabel-root": {
        fontSize: "0.875rem",
        color: theme.palette.text.secondary,
        "&.Mui-focused": {
            color: theme.palette.primary.main,
        },
    },
    "& .MuiOutlinedInput-root": {
        fontSize: "0.875rem",
        backgroundColor: theme.palette.background.paper,
        "& .MuiOutlinedInput-input": {
            height: "auto",
            color: theme.palette.text.primary,
        },
        "& fieldset": {
            borderColor: theme.palette.divider,
            borderRadius: "4px",
        },
        "&:hover fieldset": {
            borderColor: theme.palette.grey[500],
        },
        "&.Mui-focused fieldset": {
            borderColor: theme.palette.primary.main,
            borderWidth: "1px",
        },
        "&.Mui-focused": {
            boxShadow: disableFocusShadow
                ? "none"
                : `0 0 0 2px ${theme.palette.primary.main}20`,
        },
    },
    "& .MuiFormHelperText-root": {
        fontSize: "0.75rem",
        marginLeft: "0.125rem",
        marginTop: "0.25rem",
    },
}));

export type { TextFieldProps };

export const TextField = React.forwardRef<HTMLDivElement, TextFieldProps>(
    ({ disableFocusShadow, label, ...props }, ref) => {
        // Automatically disable focus shadow when there's a label
        const shouldDisableShadow = disableFocusShadow ?? Boolean(label);

        return (
            <StyledTextField
                ref={ref}
                variant="outlined"
                size="small"
                label={label}
                disableFocusShadow={shouldDisableShadow}
                {...props}
            />
        );
    }
);
TextField.displayName = "TextField";
